import classNames from "classnames";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../../../i18n/namespace";
import styles from "../../../MainHeader.module.scss";
import SlidingWidget from "../../SlidingWidget";
import { ChatContext, useChatState } from "../hooks";
import AiAssistantIcon from "./AiAssistantIcon";
import ChatBody from "./ChatBody";
import chatStyles from "./ChatContainer.module.scss";
import ChatFooter from "./ChatFooter";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";

const ChatTriggerButton = React.memo(({ buttonRef, onClick, onKeyDown, className, menuTitle }) => {
    return (
        <button
            ref={buttonRef}
            onMouseDown={e => e.preventDefault()}
            onClick={onClick}
            onKeyDown={onKeyDown}
            className={classNames(styles["trigger"], chatStyles["ai-assistant-icon-topnav"], className)}
            aria-label={menuTitle}
        >
            <AiAssistantIcon isAllBlack height={24} width={24} />
        </button>
    );
});

ChatTriggerButton.displayName = 'ChatTriggerButton';

const MenuIcon = () => (
    <AiAssistantIcon
        className={chatStyles["ai-assistant-icon"]}
        height={27.43}
        width={27}
    />
);

const ChatContainer = () => {
    const { t } = useTranslation(i18nNamespace);

    // Initialize chat state (includes connection, messages, streaming)
    const chatState = useChatState({ autoConnect: false });

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        ...chatState
    }), [chatState]);

    // Check if we can close the widget
    const canClose = useCallback(() => {
        // Could check if streaming and warn user
        return true;
    }, []);

    // Handle widget open - connect to service
    const handleWidgetOpen = useCallback(() => {
        if (!chatState.isConnected && !chatState.isConnecting) {
            chatState.connect();
        }
    }, [chatState]);

    // Trigger button renderer
    const renderTriggerButton = useCallback((props) => (
        <ChatTriggerButton {...props} />
    ), []);

    // Header with chat context
    const HeaderWithContext = useCallback(() => (
        <ChatContext.Provider value={contextValue}>
            <ChatHeader />
        </ChatContext.Provider>
    ), [contextValue]);

    // Only show footer on welcome screen (no messages)
    const hasMessages = chatState.hasMessages;

    // Use ref to persist input value across component remounts
    const inputValueRef = useRef('');

    const handleInputChange = useCallback((value) => {
        inputValueRef.current = value;
    }, []);

    // Stable ChatInputFooter component that uses context for dynamic values
    const ChatInputFooter = useCallback(() => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const chatContext = useContext(ChatContext);
        return (
            <div className={chatStyles['chat-input-footer-container']}>
                <ChatInput
                    key="footer-chat-input"
                    onSend={chatContext.sendMessage}
                    onStop={chatContext.stopGeneration}
                    disabled={false}
                    isStreaming={chatContext.isStreaming}
                    initialValue={inputValueRef.current}
                    onInputChange={handleInputChange}
                />
            </div>
        );
    }, [handleInputChange]); // Only depends on handleInputChange which is stable

    return (
        <ChatContext.Provider value={contextValue}>
            <SlidingWidget
                widgetKey="aiAssistant"
                menuTitle={t("Console assistant")}
                className={chatStyles["sliding-container"]}
                triggerClassName={styles['widget-button']}
                triggerActiveClassName={[styles['widget-button-active']]}
                TriggerButton={renderTriggerButton}
                MenuIcon={MenuIcon}
                headerClassName={chatStyles['header-menu-container']}
                HeaderContents={HeaderWithContext}
                canClose={canClose}
                MenuFooter={hasMessages ? ChatInputFooter : ChatFooter}
                onOpen={handleWidgetOpen}
            >
                <ChatBody chatState={chatState} />
            </SlidingWidget>
        </ChatContext.Provider>
    );
};

export default ChatContainer;